import { useCallback, useRef, useEffect } from 'react';
import { InterviewConfig } from '../types';

interface SarahVoiceOptions {
  config: InterviewConfig;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: Error) => void;
}

export function useSarahVoice({ config, onStart, onEnd, onError }: SarahVoiceOptions) {
  const synthRef = useRef<SpeechSynthesis | null>(null);
  const voicesLoadedRef = useRef<boolean>(false);
  const currentUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const voicesRef = useRef<SpeechSynthesisVoice[]>([]);
  const isInitializingRef = useRef<boolean>(false);

  // Initialize speech synthesis
  useEffect(() => {
    if (!window.speechSynthesis || isInitializingRef.current) return;
    
    isInitializingRef.current = true;
    synthRef.current = window.speechSynthesis;

    // Function to load and cache voices
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        voicesRef.current = voices;
        voicesLoadedRef.current = true;
        isInitializingRef.current = false;
      }
    };

    // Try to load voices immediately
    loadVoices();

    // Set up event listener for voice loading
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      if (synthRef.current?.speaking) {
        try {
          synthRef.current.cancel();
        } catch (error) {
          console.warn('Error canceling speech:', error);
        }
      }
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  // Get the appropriate female voice based on language
  const getVoice = useCallback(() => {
    if (!voicesLoadedRef.current) {
      voicesRef.current = window.speechSynthesis.getVoices();
    }

    const voices = voicesRef.current;
    const lang = config.language === 'es' ? 'es' : 'en';
    
    // Try to find a female voice for the selected language
    let voice = voices.find(v => {
      const isCorrectLang = v.lang.startsWith(lang);
      const isFemale = !v.name.toLowerCase().includes('male');
      return isCorrectLang && isFemale;
    });

    // Fallback to any voice in the correct language
    if (!voice) {
      voice = voices.find(v => v.lang.startsWith(lang));
    }

    // Final fallback to any available voice
    return voice || voices[0];
  }, [config.language]);

  const speak = useCallback(async (text: string): Promise<void> => {
    if (!text?.trim()) return;

    return new Promise((resolve, reject) => {
      if (!synthRef.current) {
        reject(new Error('Speech synthesis not available'));
        return;
      }

      try {
        // Cancel any ongoing speech
        if (synthRef.current.speaking) {
          synthRef.current.cancel();
        }

        // Small delay to ensure previous speech is canceled
        setTimeout(() => {
          try {
            const utterance = new SpeechSynthesisUtterance(text);
            const voice = getVoice();
            
            if (voice) {
              utterance.voice = voice;
            }

            // Apply Sarah's voice configuration
            utterance.pitch = config.voice?.pitch || 1.1;
            utterance.rate = config.voice?.rate || 0.9;
            utterance.volume = 1;
            utterance.lang = config.language === 'es' ? 'es-ES' : 'en-US';

            // Event handlers
            utterance.onstart = () => {
              onStart?.();
            };

            utterance.onend = () => {
              currentUtteranceRef.current = null;
              onEnd?.();
              resolve();
            };

            utterance.onerror = (event) => {
              currentUtteranceRef.current = null;
              const error = new Error(`Speech synthesis error: ${event.error}`);
              onError?.(error);
              reject(error);
            };

            // Store current utterance and speak
            currentUtteranceRef.current = utterance;
            synthRef.current?.speak(utterance);

          } catch (error) {
            currentUtteranceRef.current = null;
            const speechError = error instanceof Error ? error : new Error('Speech synthesis failed');
            onError?.(speechError);
            reject(speechError);
          }
        }, 100);

      } catch (error) {
        currentUtteranceRef.current = null;
        const speechError = error instanceof Error ? error : new Error('Speech synthesis failed');
        onError?.(speechError);
        reject(speechError);
      }
    });
  }, [config.language, config.voice, getVoice, onStart, onEnd, onError]);

  const stop = useCallback(() => {
    try {
      if (synthRef.current?.speaking) {
        synthRef.current.cancel();
      }
      currentUtteranceRef.current = null;
    } catch (error) {
      console.warn('Error stopping speech:', error);
    }
  }, []);

  const isPlaying = useCallback(() => {
    return synthRef.current?.speaking || false;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return {
    speak,
    stop,
    isPlaying
  };
}